import { DataTableProps } from '../components';

export interface GetColumnsExportParams {
  selectedView?: DataTableProps.Columns | null;
  viewOptions?: DataTableProps.ViewOptions;
}

const getColumnsForExport = (params: GetColumnsExportParams) => {
  const { selectedView, viewOptions } = params;
  const allCols = viewOptions
    ? (viewOptions.flatMap((v) => v.columns) as DataTableProps.Columns)
    : ((selectedView || []) as DataTableProps.Columns);

  const uniqueMap = new Map<string, { key: string; label: string }>();

  allCols.forEach((col) => {
    const key = String(col.key || '');
    if (!key) return;
    // Skip actions column from export
    if ((col as any).type === 'actions') return;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, { key, label: col.label || key });
    }
  });

  return Array.from(uniqueMap.values());
};

const getValueByPath = (obj: any, path: string) => {
  try {
    const parts = path.split('.');
    let cur = obj;
    // eslint-disable-next-line no-restricted-syntax
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }

    if (typeof cur !== 'undefined' && cur !== null) return cur;

    if (path === 'profile_name') {
      const name0 = obj.profile_name;
      const name1 = obj.influencer?.profile_name;
      const name2 = obj.influencer_profile_name;

      return name0 || name1 || name2;
    }

    if (path === 'status') {
      return obj.brand_infos.map(
        (b: any) => `${b.brand} - ${b.country_team} - ${b.status}`,
      );
    }

    return 0;
  } catch (e) {
    return undefined;
  }
};

export interface BuildCsvParams extends GetColumnsExportParams {
  data: any[];
}

const buildCsv = (params: BuildCsvParams) => {
  const { data, ...rest } = params;
  const cols = getColumnsForExport(rest);
  const rows = data || [];
  const header = cols
    .map((c) => `"${String(c.label).replace(/"/g, '""')}"`)
    .join(',');

  const body = rows
    .map((row: any) =>
      cols
        .map(({ key }) => {
          const val = getValueByPath(row, key);
          let out: any = val;
          if (Array.isArray(val)) {
            out = val
              .map((v) =>
                v != null && typeof v === 'object' ? JSON.stringify(v) : v,
              )
              .join('; ');
          } else if (val != null && typeof val === 'object') {
            out = JSON.stringify(val);
          }
          if (out === null || out === undefined) out = '';
          return `"${String(out).replace(/"/g, '""')}"`;
        })
        .join(','),
    )
    .join('\n');

  return header + (body ? `\n${body}` : '');
};

export interface ExportParams extends BuildCsvParams {
  name?: string;
}

export const downloadCsvBlob = (blob: Blob, name?: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileSafeName = (name || 'export').toLowerCase().replace(/\s+/g, '_');
  link.setAttribute('download', `${fileSafeName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const handleExport = (params: ExportParams) => {
  const { name, ...rest } = params;
  const csv = buildCsv(rest);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadCsvBlob(blob, name);
};

export default handleExport;
